import React, { ReactElement, useMemo } from 'react';
import { Button, MenuItem, Select, useMediaQuery } from '@mui/material';

import { EConnectionType } from 'modules/auth/const';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { MATCH_ID_LINK } from 'modules/common/const';
import { useCopyClick } from 'modules/common/hooks/useCopyClick';
import { ShareIcon } from 'modules/common/icons';
import { cropString } from 'modules/common/utils/cropString';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { globalTranslation } from 'modules/i18n';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import BurgerIcon from '../../assets/burger.svg?react';
import CloseIcon from '../../assets/close.svg?react';
import { MIN_BUTTON_WIDTH, useStyles } from './useStyles';

interface IAccountProps {
  showMenu: boolean;
  isShowBurger?: boolean;
  onShowMenu: (showMenu: boolean) => void;
}

export function Account({
  showMenu,
  onShowMenu,
  isShowBurger = false,
}: IAccountProps): ReactElement {
  const { keys, t } = useTranslation(globalTranslation);
  const { classes, theme } = useStyles();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  const {
    isConnected,
    accountAddress,
    disconnect,
    walletIcon,
    connectionType,
  } = useConnection();

  const { onCopyClick, isCopied } = useCopyClick();

  const { onOpen } = useDialog(KnownDialogs.connect);
  const menuButton = useMemo(
    () =>
      isMd &&
      isShowBurger && (
        <Button
          className={classes.squareButton}
          color="secondary"
          variant="outlined"
          onClick={() => onShowMenu(!showMenu)}
        >
          {showMenu ? <CloseIcon /> : <BurgerIcon />}
        </Button>
      ),
    [classes.squareButton, isMd, isShowBurger, onShowMenu, showMenu],
  );

  if (!isConnected) {
    return (
      <div className={classes.root}>
        <Button
          color="secondary"
          sx={{ minWidth: MIN_BUTTON_WIDTH }}
          variant="outlined"
          onClick={onOpen}
        >
          {t(keys.common.connectWallet)}
        </Button>

        {menuButton}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Select
        classes={{ root: classes.selectRoot, select: classes.select }}
        MenuProps={{ sx: { mt: 1 } }}
        renderValue={() => (
          <>
            {walletIcon && (
              <img alt="" className={classes.walletIcon} src={walletIcon} />
            )}

            <span>{accountAddress && cropString(accountAddress)}</span>
          </>
        )}
        value={accountAddress}
        variant="standard"
      >
        {connectionType === EConnectionType.MatchId && (
          <MenuItem
            component="a"
            href={MATCH_ID_LINK}
            rel="noreferrer"
            target="_blank"
          >
            <span>{t(keys.common.openMatchID)}</span>

            <ShareIcon />
          </MenuItem>
        )}

        <MenuItem onClick={() => accountAddress && onCopyClick(accountAddress)}>
          {isCopied ? t(keys.common.copied) : t(keys.common.copyAccount)}
        </MenuItem>

        <MenuItem onClick={() => disconnect()}>
          {t(keys.common.disconnect)}
        </MenuItem>
      </Select>

      {menuButton}
    </div>
  );
}
